/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Dev-cockpit integration for reCAPTCHA: lazy-loads inspector/simulator stores
 * and reports reCAPTCHA requests/simulations when the cockpit is enabled.
 */

import { reportAntiBotError } from '@/context/AntiBotContext';

// Dev cockpit imports use Vite's static `import.meta.glob` so they are tree-shaken
// from production builds via the FF_DEV_COCKPIT guard. We intentionally avoid
// `require(...)` here: it is undefined in the browser ESM runtime and would throw
// `ReferenceError: require is not defined`, silently disabling cockpit reporting.
type StateSimulatorModule = typeof import('@/features/dev-cockpit/services/state-simulator');
type ApiInterceptorModule = typeof import('@/features/dev-cockpit/services/api-interceptor');
type NetworkInterceptorModule = typeof import('@/features/dev-cockpit/services/global-network-interceptor');

const _simModules = import.meta.glob<StateSimulatorModule>(
  '@/features/dev-cockpit/services/state-simulator.ts',
  { eager: true },
);
const _apiModules = import.meta.glob<ApiInterceptorModule>(
  '@/features/dev-cockpit/services/api-interceptor.ts',
  { eager: true },
);
const _netModules = import.meta.glob<NetworkInterceptorModule>(
  '@/features/dev-cockpit/services/global-network-interceptor.ts',
  { eager: true },
);

let _devCockpitModules: {
  useStateSimulatorStore: StateSimulatorModule['useStateSimulatorStore'];
  useApiInterceptorStore: ApiInterceptorModule['useApiInterceptorStore'];
  generateRequestId: ApiInterceptorModule['generateRequestId'];
  markCaptured: NetworkInterceptorModule['markCaptured'];
} | null = null;
let _devCockpitResolved = false;

/**
 * Resolves the dev-cockpit reporting modules eagerly loaded via `import.meta.glob`,
 * or `null` when the cockpit build flag is off or any module is missing.
 *
 * @returns The cockpit module bundle, or `null` when unavailable.
 */
function resolveModules(): typeof _devCockpitModules {
  if (import.meta.env.FF_DEV_COCKPIT !== 'true') return null;
  const sim = Object.values(_simModules)[0];
  const api = Object.values(_apiModules)[0];
  const net = Object.values(_netModules)[0];
  if (!sim || !api || !net) return null;
  return {
    useStateSimulatorStore: sim.useStateSimulatorStore,
    useApiInterceptorStore: api.useApiInterceptorStore,
    generateRequestId: api.generateRequestId,
    markCaptured: net.markCaptured,
  };
}

/**
 * Lazily resolves and memoizes the dev-cockpit modules on first access.
 *
 * @returns The cockpit module bundle, or `null` when the cockpit is disabled.
 */
export function getDevCockpit(): typeof _devCockpitModules {
  if (_devCockpitResolved) return _devCockpitModules;
  _devCockpitModules = resolveModules();
  _devCockpitResolved = true;
  return _devCockpitModules;
}

/**
 * Reports a reCAPTCHA request (success or error) to the dev-cockpit API
 * inspector so it appears in the request history. No-op without the cockpit.
 *
 * @param siteKey reCAPTCHA site key used to build the synthetic request URL.
 * @param status HTTP-like status to record (0 for init failures).
 * @param error Optional error detail; when present the request is marked failed.
 */
export function reportRecaptchaToInspector(siteKey: string, status: number, error?: string): void {
  const dc = getDevCockpit();
  if (!dc) return;
  try {
    const store = dc.useApiInterceptorStore.getState();
    const id = dc.generateRequestId();
    const url = `https://www.google.com/recaptcha/enterprise/pat?k=${siteKey}`;

    dc.markCaptured(url);

    store.onRequestStart({
      id,
      timestamp: Date.now(),
      method: 'POST·recaptcha',
      url,
      requestHeaders: {},
      requestBody: undefined,
    });

    if (error) {
      store.onRequestError(id, `${status} — ${error}`);
    } else {
      store.onRequestEnd(id, {
        status,
        responseHeaders: {},
        responseBody: undefined,
      });
    }
  } catch {
    /* API interceptor not available */
  }
}

/**
 * If the dev-cockpit state simulator has the reCAPTCHA-401 toggle on, emits a
 * simulated 401 anti-bot error (and inspector entry) and reports it as handled.
 *
 * @param siteKey reCAPTCHA site key used for the inspector entry.
 * @returns `true` when a simulated failure was raised, else `false`.
 */
export function checkRecaptchaSimulation(siteKey: string): boolean {
  const dc = getDevCockpit();
  if (!dc || !dc.useStateSimulatorStore.getState().simulateRecaptcha401) return false;

  const simError = new Error('Simulated reCAPTCHA 401 Unauthorized');
  (simError as any).status = 401;
  if (siteKey) reportRecaptchaToInspector(siteKey, 401, 'Simulated reCAPTCHA 401 Unauthorized');
  reportAntiBotError(simError, 401);
  return true;
}
